import Button from "devextreme-react/button";
import { Popup } from "devextreme-react/popup";
import TagBox from "devextreme-react/tag-box";
import React, { useEffect, useState } from "react";
import { UserRole } from "../../../../dtos/UserRole";

type RoleGroups = { key: string; items: { id: UserRole; name: string }[] }[];

// Adds rows to a group in one step: which rows, and the roles to put on them. The rows a tab can
// add are fixed (the statuses of an action, the transitions of a label), so this only ever offers
// the ones not already on screen.
type Props = {
    visible: boolean;
    title: string;
    rowLabel: string;
    rowItems: { id: string; name: string }[];
    roleItems: RoleGroups;
    onCancel: () => void;
    onAdd: (rowKeys: string[], roles: UserRole[]) => void;
};

export default function AddRowsDialog({ visible, title, rowLabel, rowItems, roleItems, onCancel, onAdd }: Props) {
    const [rows, setRows] = useState<string[]>([]);
    const [roles, setRoles] = useState<UserRole[]>([]);

    useEffect(() => {
        if (visible) {
            setRows([]);
            setRoles([]);
        }
    }, [visible]);

    // Adding with no role picked would leave a row the save drops again
    const canAdd = rows.length > 0 && roles.length > 0;

    return (
        <Popup
            visible={visible}
            onHiding={onCancel}
            showCloseButton
            dragEnabled
            title={title}
            width={600}
            height="auto"
        >
            <div style={{ display: "grid", gap: "16px", padding: "8px" }}>
                <div>
                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>{rowLabel}</label>
                    <TagBox
                        items={rowItems}
                        displayExpr="name"
                        valueExpr="id"
                        value={rows}
                        onValueChanged={(e) => setRows(e.value ?? [])}
                        width="100%"
                        placeholder={`Select ${rowLabel.toLowerCase()}...`}
                        showSelectionControls={true}
                    />
                </div>

                <div>
                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Roles</label>
                    <TagBox
                        items={roleItems}
                        displayExpr="name"
                        valueExpr="id"
                        grouped={true}
                        value={roles}
                        onValueChanged={(e) => setRoles(e.value ?? [])}
                        width="100%"
                        placeholder="Select roles..."
                        showSelectionControls={true}
                    />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}>
                    <Button text="Cancel" stylingMode="outlined" onClick={onCancel} />
                    <Button
                        text="Add"
                        type="default"
                        stylingMode="contained"
                        disabled={!canAdd}
                        onClick={() => onAdd(rows, roles)}
                    />
                </div>
            </div>
        </Popup>
    );
}
